/**
 * API Service
 * Centralized service for making API calls to the backend
 */
import axios from 'axios';
import { API_CONFIG, buildApiUrl } from '../config/api.js';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.REQUEST_CONFIG.TIMEOUT,
  headers: API_CONFIG.REQUEST_CONFIG.HEADERS
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    if (import.meta.env.DEV) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Silently ignore 404 errors from crawl status endpoints (expected when no crawl is active)
    const url = error.config?.url || '';
    const is404 = error.response?.status === 404;
    const isCrawlStatusEndpoint = url.includes('/crawl/status/current');
    
    if (!(is404 && isCrawlStatusEndpoint)) {
      console.error('❌ API Response Error:', error.response?.status, error.message);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Data API Service
 */
export const dataApi = {
  /**
   * Extract data from a single URL
   */
  async extractSingle(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.JUGENDAMT.EXTRACT, data);
    return response.data;
  },

  /**
   * Extract data from a single URL with streaming updates
   */
  async extractSingleStream(data, onUpdate) {
    const url = `${apiClient.defaults.baseURL}${API_CONFIG.ENDPOINTS.JUGENDAMT.EXTRACT_STREAM}`;
    
    if (import.meta.env.DEV) {
      console.log(`🚀 Streaming Single Extraction: POST ${url}`);
    }
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const updateData = JSON.parse(line.slice(6));
              onUpdate(updateData);
            } catch (e) {
              console.error('Error parsing streaming data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      throw error;
    }
  },

  /**
   * Extract data from a single URL (simple, non-streaming)
   */
  async extractSimple(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.JUGENDAMT.EXTRACT, data);
    return response.data;
  },

  /**
   * Extract data from multiple URLs (batch)
   */
  async extractBatch(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.JUGENDAMT.EXTRACT_BATCH, data);
    return response.data;
  },

  /**
   * Auto search and extract (non-streaming)
   */
  async autoSearch(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.JUGENDAMT.AUTO_SEARCH, data);
    return response.data;
  },

  /**
   * Auto search and extract (streaming)
   */
  async autoSearchStream(data, onUpdate) {
    // Use the configured base URL from apiClient
    const url = `${apiClient.defaults.baseURL}${API_CONFIG.ENDPOINTS.JUGENDAMT.AUTO_SEARCH_STREAM}`;
    /** If we already got crawl_complete, stream closed afterward is OK (e.g. inference path). */
    let lastUpdateType = null;

    if (import.meta.env.DEV) {
      console.log(`🚀 Streaming API Request: POST ${url}`);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamError = null;
      const wrappedOnUpdate = (data) => {
        if (data && data.type) lastUpdateType = data.type;
        onUpdate(data);
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) {
            const lines = buffer.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const updateData = JSON.parse(line.slice(6));
                  wrappedOnUpdate(updateData);
                  if (updateData.type === 'error') streamError = updateData.message || 'Unbekannter Fehler';
                } catch (e) {
                  if (import.meta.env.DEV) console.error('Error parsing final streaming data:', e, 'Line:', line);
                }
              }
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';

        for (const message of messages) {
          const lines = message.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const updateData = JSON.parse(line.slice(6));
                wrappedOnUpdate(updateData);
                if (updateData.type === 'error') streamError = updateData.message || 'Unbekannter Fehler';
              } catch (e) {
                if (import.meta.env.DEV) console.error('Error parsing streaming data:', e, 'Line:', line);
              }
            }
          }
          if (streamError) break;
        }
        if (streamError) break;
      }

      if (streamError) throw new Error(streamError);
    } catch (error) {
      const msg = error?.message || '';
      const isInputStreamError = msg.includes('input stream') || error?.name === 'TypeError';
      if (isInputStreamError && lastUpdateType === 'crawl_complete') {
        return;
      }
      console.error('Streaming error:', error);
      if (isInputStreamError || msg.includes('fetch') || msg.includes('network')) {
        throw new Error('Verbindung zum Server unterbrochen (z.B. Timeout). Bitte später erneut versuchen.');
      }
      throw error;
    }
  },

  /**
   * Regional Search Stream - Real-time updates for regional search
   */
  async regionalSearchStream(data, onUpdate) {
    const url = `${apiClient.defaults.baseURL}/data/extract-regional-stream`;
    
    if (import.meta.env.DEV) {
      console.log(`🌍 Streaming Regional Search: POST ${url}`);
    }
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      /** When backend sends type:'error', we stop reading and reject with this so the stream ends cleanly (no "Error in input stream"). */
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const lines = buffer.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const updateData = JSON.parse(line.slice(6));
                  onUpdate(updateData);
                  if (updateData.type === 'error') {
                    streamError = updateData.message || 'Unbekannter Fehler';
                  }
                } catch (e) {
                  if (import.meta.env.DEV) {
                    console.error('Error parsing final regional streaming data:', e, 'Line:', line);
                  }
                }
              }
            }
          }
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Split by double newline (SSE message delimiter)
        const messages = buffer.split('\n\n');
        // Keep the last incomplete message in buffer
        buffer = messages.pop() || '';

        // Process complete messages
        for (const message of messages) {
          const lines = message.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6);
                const updateData = JSON.parse(jsonStr);
                onUpdate(updateData);
                if (updateData.type === 'error') {
                  streamError = updateData.message || 'Unbekannter Fehler';
                  break;
                }
              } catch (e) {
                if (import.meta.env.DEV) {
                  console.error('Error parsing regional streaming data:', e, 'Line:', line);
                }
              }
            }
          }
          if (streamError) break;
        }
        if (streamError) break;
      }

      if (streamError) {
        throw new Error(streamError);
      }
    } catch (error) {
      console.error('Regional streaming error:', error);
      const msg = error?.message || '';
      if (msg.includes('input stream') || msg.includes('fetch') || msg.includes('network') || error?.name === 'TypeError') {
        throw new Error('Verbindung zum Server unterbrochen (z.B. Timeout). Bitte mit weniger Städten erneut versuchen oder später erneut starten.');
      }
      throw error;
    }
  },

  /**
   * Excel Batch Processing
   */
  async excelBatchProcessing(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.JUGENDAMT.EXCEL_BATCH, data);
    return response.data;
  },

  /**
   * Get crawl history
   */
  async getHistory(params = {}) {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.JUGENDAMT.HISTORY, { params });
    return response.data;
  },

  /**
   * Get specific crawl session
   */
  async getSession(sessionId) {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.JUGENDAMT.HISTORY_SESSION(sessionId));
    return response.data;
  },

  /**
   * Get crawl results for a session
   */
  async getSessionResults(sessionId) {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.JUGENDAMT.HISTORY_RESULTS(sessionId));
    return response.data;
  },

  /**
   * Get crawl statistics
   */
  async getStats() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.JUGENDAMT.STATS);
    return response.data;
  },

  /**
   * Delete a crawl session
   */
  async deleteSession(sessionId) {
    const response = await apiClient.delete(API_CONFIG.ENDPOINTS.JUGENDAMT.DELETE_SESSION(sessionId));
    return response.data;
  },

  /**
   * Cleanup old sessions
   * DISABLED: Use backend scripts for cleanup instead to prevent accidental data loss
   */
  // async cleanupSessions(daysOld = 30) {
  //   const response = await apiClient.delete(API_CONFIG.ENDPOINTS.JUGENDAMT.CLEANUP, {
  //     params: { days_old: daysOld }
  //   });
  //   return response.data;
  // },

  /**
   * Health check
   */
  async healthCheck() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.JUGENDAMT.HEALTH);
    return response.data;
  }
};

/**
 * Crawl API Service
 */
export const crawlApi = {
  /**
   * Start a new crawl
   */
  async startCrawl(data) {
    const response = await apiClient.post(API_CONFIG.ENDPOINTS.CRAWL.START, data);
    return response.data;
  },

  /**
   * Get crawl status
   */
  async getCrawlStatus(crawlId) {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.CRAWL.STATUS(crawlId));
    return response.data;
  },

  /**
   * List all crawls
   */
  async listCrawls() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.CRAWL.LIST);
    return response.data;
  },

  /**
   * Get current active crawl status
   */
  async getCurrentCrawlStatus() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.CRAWL.CURRENT_STATUS);
    return response.data;
  },

  /**
   * Check if any crawl is active
   */
  async isCrawlActive() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.CRAWL.ACTIVE_STATUS);
    return response.data;
  }
};

/**
 * General API Service
 */
export const generalApi = {
  /**
   * Root endpoint
   */
  async getRoot() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.ROOT);
    return response.data;
  },

  /**
   * Health check
   */
  async healthCheck() {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.HEALTH);
    return response.data;
  }
};

// Export the main API client for custom requests
export { apiClient };

export default {
  data: dataApi,
  crawl: crawlApi,
  general: generalApi
};
